import moment from "moment";
import PropTypes from "prop-types";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { RillContext } from "../../rill/RillReact/share/base";
import { describeInsert, getInsertSession } from "../../utils/exercise-insert";
import getCycleCardImagePath from "../../utils/get-cycle-card-image-path";
import ImgComponent from "../ImgComponent";
import Select from "../inputs/select";
import Modal from "../modal-new";
import SvgIcon from "../SvgIcon";
import InsertPositionCard from "./add_edit_exercise/InsertPositionCard";
import { getExerciseName } from "./learn-more/blockHelpers";

/**
 * Count the exercises across all sessions of a saved single-workout template.
 * Single workouts have a single program with (usually) a single session.
 */
const countWorkoutExercises = (workout) =>
  (workout?.programs?.[0]?.sessions || []).reduce(
    (total, session) => total + (session.exercises?.length || 0),
    0
  );

/**
 * Workouts visible for the current folder tab. `null` selectedFolderId means
 * "View All" — the full flat list, unfiltered.
 */
const getVisibleWorkouts = (
  savedWorkouts,
  workoutFolders,
  selectedFolderId
) => {
  if (!selectedFolderId) return savedWorkouts;
  const folder = (workoutFolders || []).find(
    (f) => String(f._id) === String(selectedFolderId)
  );
  if (!folder) return savedWorkouts;
  const folderIds = new Set((folder.workoutIds || []).map(String));
  return (savedWorkouts || []).filter((w) => folderIds.has(String(w._id)));
};

/**
 * "Import Workout" modal for the program editor.
 *
 * Lists the coach's saved Single Workout templates (locals.savedWorkouts). Selecting
 * one appends its exercises to the target session via /actions/exercise/import-workout,
 * which reuses the same pipeline as "Paste Workout". When the coach has no saved
 * workouts, an empty state links to the Single Workouts templates page.
 */
const ImportWorkoutModal = ({
  savedWorkouts = [],
  workoutFolders = [],
  cycleId,
  programNumber,
  programWeek,
  programSession,
  date,
  exercisesLength = 0,
  highestOrder = 0,
  athlete = {},
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);

  const { locals } = useContext(RillContext);
  // Insert mode: opened from the exercise ••• menu with insertTarget +
  // insertPosition. Anything else (the plain "Import workout" button, the
  // calendar) keeps the single-select append flow.
  const { insertTarget, insertPosition: initialPosition } = locals.query || {};
  // Program editor: the cycle-wide lists; calendar: the athlete's day.
  const insertSession =
    insertTarget && cycleId
      ? getInsertSession(
          locals.editExercises,
          locals.editSections,
          insertTarget
        )
      : insertTarget && date
        ? getInsertSession(
            locals.activeExercises,
            locals.activeSections,
            insertTarget,
            { day: date }
          )
        : null;
  const [insertPosition, setInsertPosition] = useState(
    initialPosition === "above" ? "above" : "below"
  );

  // Selected templates, in the order they were picked (that is the import
  // order in insert mode, where several can be selected).
  const [selectedWorkouts, setSelectedWorkouts] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);

  const handleFolderSelect = (folderId) => {
    setSelectedFolderId(folderId);
  };

  const toggleWorkout = (workout) => {
    setSelectedWorkouts((current) => {
      const isSelected = current.some((w) => w._id === workout._id);
      if (!insertSession) return isSelected ? [] : [workout];
      return isSelected
        ? current.filter((w) => w._id !== workout._id)
        : [...current, workout];
    });
  };

  const selectedCount = selectedWorkouts.reduce(
    (total, workout) => total + countWorkoutExercises(workout),
    0
  );
  const insertView = insertSession
    ? describeInsert({
        exercises: insertSession.exercises,
        sections: insertSession.sections,
        targetId: insertSession.target._id,
        position: insertPosition,
        // one plain exercise per imported exercise: only the join state and
        // the target's letter matter here
        queueItems: Array.from({ length: Math.max(selectedCount, 1) }, () => ({
          kind: "exercise",
        })),
      })
    : null;

  const handleImport = () => {
    if (selectedWorkouts.length === 0) return;
    const [selectedWorkout] = selectedWorkouts;

    // A cycleId means we're in the program editor (import into a session); otherwise
    // we're on the calendar and import onto a day.
    const target = cycleId
      ? {
          cycleId,
          programNumber: Number(programNumber),
          sessionProgram: {
            week: Number(programWeek),
            order: Number(programSession),
          },
        }
      : { date };

    submit(`/actions/exercise/import-workout`, {
      method: "POST",
      body: {
        workoutCycleId: selectedWorkout._id,
        ...(insertSession
          ? {
              workoutCycleIds: selectedWorkouts.map((w) => w._id),
              workoutNames: selectedWorkouts.map((w) => w.name),
              insert: {
                targetExerciseId: insertSession.target._id,
                position: insertPosition,
              },
            }
          : {}),
        ...target,
        exercisesLength: Number(exercisesLength) || 0,
        highestOrder: Number(highestOrder) || 0,
        athlete: { _id: athlete._id, repMaximums: athlete.repMaximums },
      },
    });
  };

  const goToTemplates = (e) => {
    e.preventDefault();
    submit(`/coaching-portal/all-templates`, {
      method: "GET",
      body: {
        owner: "coach",
        tabView: "single-workouts-tab",
        templateView: "single-workouts",
        status: "draft",
      },
    });
  };

  const hasWorkouts = Array.isArray(savedWorkouts) && savedWorkouts.length > 0;
  const hasFolders = Array.isArray(workoutFolders) && workoutFolders.length > 0;
  const visibleWorkouts = getVisibleWorkouts(
    savedWorkouts,
    workoutFolders,
    selectedFolderId
  );

  const folderOptions = [
    {
      text: `${t("athlete-portal:programs.importWorkoutViewAll")} (${
        savedWorkouts.length
      })`,
      value: "",
    },
    ...workoutFolders.map((folder) => ({
      text: `${folder.name} (${
        getVisibleWorkouts(savedWorkouts, workoutFolders, folder._id).length
      })`,
      value: String(folder._id),
    })),
  ];

  return (
    <Modal
      className={`import-workout-modal${hasWorkouts ? " has-workouts" : ""}`}
      title={t("athlete-portal:programs.importWorkoutTitle")}
      toClear={["insertTarget", "insertPosition"]}
    >
      <div className="body max-w-lg">
        {hasWorkouts ? (
          <>
            <p className="p4 opacity-70 mb-4">
              {t("athlete-portal:programs.importWorkoutDescription")}
            </p>
            {insertView && (
              <div className="mb-4">
                <InsertPositionCard
                  position={insertPosition}
                  onPositionChange={setInsertPosition}
                  targetLetter={insertView.targetLetterNow}
                  targetName={getExerciseName(insertSession.target)}
                  view={insertView}
                  contextPrefix={
                    cycleId
                      ? t("athlete-portal:programs.insert.workoutContext", {
                          week: programWeek,
                          workout: programSession,
                        })
                      : moment.utc(date).format("ddd, MMM D")
                  }
                />
              </div>
            )}
            {hasFolders && (
              <div className="mb-3">
                <Select
                  name="importWorkoutFolder"
                  title={t("athlete-portal:programs.importWorkoutFolderLabel")}
                  value={selectedFolderId ? String(selectedFolderId) : ""}
                  onChange={(val) => handleFolderSelect(val || null)}
                  options={folderOptions}
                  disabled={false}
                  overlay={true}
                />
              </div>
            )}
            <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto">
              {visibleWorkouts.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-8 text-center">
                  <p className="p4 opacity-70">
                    {t(
                      "athlete-portal:programs.importWorkoutFolderEmptyDescription"
                    )}
                  </p>
                  <button
                    type="button"
                    className="p4 underline opacity-70"
                    onClick={() => handleFolderSelect(null)}
                  >
                    {t("athlete-portal:programs.importWorkoutViewAll")}
                  </button>
                </div>
              ) : (
                visibleWorkouts.map((workout) => {
                  const selectedAt = selectedWorkouts.findIndex(
                    (w) => w._id === workout._id
                  );
                  const isSelected = selectedAt !== -1;
                  const image = getCycleCardImagePath(workout);
                  const imageClassNames = image.startsWith("data:image")
                    ? "no-image"
                    : "";

                  return (
                    <article
                      key={workout._id}
                      className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${
                        isSelected
                          ? "border-(--primary) bg-(--white-05)"
                          : "border-(--border-color)"
                      }`}
                      onClick={() => toggleWorkout(workout)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          toggleWorkout(workout);
                      }}
                    >
                      <div className="w-20 h-14 flex-shrink-0 overflow-hidden rounded-md [&_img]:w-full [&_img]:h-full [&_img]:object-cover">
                        <ImgComponent
                          image={image}
                          classNames={imageClassNames}
                          altText="workout image"
                        />
                      </div>
                      <div className="flex flex-col flex-1">
                        <span className="s1">{workout.name}</span>
                        <span className="p4 opacity-70">
                          {t("athlete-portal:programs.importWorkoutExercises", {
                            count: countWorkoutExercises(workout),
                          })}
                        </span>
                      </div>
                      {insertSession &&
                        isSelected &&
                        selectedWorkouts.length > 1 && (
                          <span className="calendar-prefix">
                            {selectedAt + 1}
                          </span>
                        )}
                      <input
                        type="checkbox"
                        className="w-6 h-6 cursor-pointer flex-shrink-0"
                        checked={isSelected}
                        readOnly
                      />
                    </article>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <SvgIcon
              src="/img/new-icons/dumbbell-dark.svg"
              className="h-11 w-11"
              noFilterOnLight={true}
            />
            <p className="s1">
              {t("athlete-portal:programs.importWorkoutEmptyTitle")}
            </p>
            <p className="p4 opacity-70 mb-2">
              {t("athlete-portal:programs.importWorkoutEmptyDescription")}
            </p>
            <button
              type="button"
              data-button-type="primary"
              onClick={goToTemplates}
            >
              {t("athlete-portal:programs.importWorkoutEmptyButton")}
            </button>
          </div>
        )}
      </div>

      {hasWorkouts && (
        <footer>
          <button
            type="button"
            data-button-type="secondary"
            data-action="cancel"
            onClick={() => {
              submit("/actions/refresh", {
                method: "GET",
                body: { modal: "", insertTarget: "", insertPosition: "" },
              });
            }}
          >
            {t("common:cancel")}
          </button>
          <button
            type="button"
            data-button-type="primary"
            disabled={selectedWorkouts.length === 0}
            onClick={handleImport}
          >
            {insertView
              ? t(
                  `athlete-portal:programs.insert.${
                    insertPosition === "above"
                      ? "importButtonAbove"
                      : "importButtonBelow"
                  }`,
                  { letter: insertView.targetLetterNow }
                )
              : t("athlete-portal:programs.importWorkoutAdd")}
          </button>
        </footer>
      )}
    </Modal>
  );
};

ImportWorkoutModal.propTypes = {
  savedWorkouts: PropTypes.array,
  workoutFolders: PropTypes.array,
  cycleId: PropTypes.string,
  programNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  programWeek: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  programSession: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  date: PropTypes.string,
  exercisesLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  highestOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  athlete: PropTypes.object,
};

export default ImportWorkoutModal;
